import { useMemo } from 'react';
import { useDevicesControlUnits } from '../../controlUnit/hooks/useDevicesControlUnits';
import { getCUsTranslationsSelector } from '../selectors';
import { useAppSelector } from '../../store.model';
import CUTranslationsAPI from '../api/CUTranslationAPI';
import useMultipleQueries from '../../../util/rtk/useQueries';
import { skipToken } from '@reduxjs/toolkit/query';
import { ControlUnitTranslationStaticAPIResponse } from '../api/CUTranslation.model';
import { isNotNullOrUndefined } from '../../../util/types/Array';
import { orComparator } from '../../../util/lodash/orComparator';
import _ from 'lodash';

export const useCUsTranslate = () => {
  const [devicesControlUnits, loadingDevicesControlUnits] =
    useDevicesControlUnits();

  // Fetch commander translation info and file
  const { data: commanderTranslationInfo, ...restCommanderTranslations } =
    CUTranslationsAPI.useGetCommanderTranslationInfoQuery(undefined);

  const argsDevicesMemo = useMemo(
    () =>
      devicesControlUnits.flatMap(
        (dcu) =>
          dcu?.controlUnits
            ?.map((controlUnit) => ({
              deviceId: dcu.deviceId,
              controlUnitId: controlUnit.id,
            }))
            .filter(isNotNullOrUndefined) ?? []
      ),
    [devicesControlUnits]
  );

  const { data: controlUnitInfos, ...restControlUnitInfos } =
    useMultipleQueries<
      any,
      ControlUnitTranslationStaticAPIResponse,
      {
        deviceId: string;
        controlUnitId: string | number;
      }
    >(CUTranslationsAPI.endpoints.getCUTranslationInfo, argsDevicesMemo, {
      skip: argsDevicesMemo.length === 0 || loadingDevicesControlUnits,
    });

  const { data: commanderTranslationData, ...restCommanderFileTranslations } =
    CUTranslationsAPI.useDownloadTranslationFileQuery(
      commanderTranslationInfo ?? skipToken
    );

  const argsFilesMemo = useMemo(
    () =>
      controlUnitInfos
        ? controlUnitInfos
            .map((info) => info?.file)
            .filter((file): file is NonNullable<typeof file> => !!file)
        : [],
    [controlUnitInfos]
  );
  const { data: controlUnitTranslationDataArray, ...restControlUnitFiles } =
    useMultipleQueries<any, any, { url: string }>(
      CUTranslationsAPI.endpoints.downloadTranslationFile,
      argsFilesMemo,
      {
        skip: argsFilesMemo.length === 0 || loadingDevicesControlUnits,
      }
    );

  const CUsTranslate = useAppSelector(getCUsTranslationsSelector);
  const response = useMemo(
    () =>
      _.chain({
        ...restCommanderTranslations,
        CUsTranslate,
        data: CUsTranslate,
      })
        .mergeWith(restControlUnitInfos, orComparator)
        .mergeWith(restCommanderFileTranslations, orComparator)
        .mergeWith(restControlUnitFiles, orComparator)
        .value(),
    [
      restCommanderTranslations,
      CUsTranslate,
      restControlUnitInfos,
      restCommanderFileTranslations,
      restControlUnitFiles,
    ]
  );

  return [
    CUsTranslate,
    response.isLoading || loadingDevicesControlUnits,
  ] as const;
};
